import { PermissionGrantActionsMultiSelect } from './permission-grant-actions-multi-select.component'
import { Controller, useFormContext } from 'react-hook-form'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CurecallButton,
  CurecallInputLabel,
  CurecallTooltip,
  CurecallToggle,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  subjectToIntelDescription,
  subjectToIntlTitle,
  TPermissionFormValues,
} from './permissions.type'
import { getAvailableActionsForSubject, updatePermissionGrants } from './permissions.util'
import { useCurrentProject } from 'src/providers/current-project.provider'

function PermissionsTableFields() {
  const { t } = useTranslation()
  const { control } = useFormContext<TPermissionFormValues>()
  const { isFeatureActivated } = useCurrentProject()

  const id = useId()

  const visibleSubjects = Object.values(EPermissionGrantSubjects).filter(
    (subject) =>
      subject !== EPermissionGrantSubjects.ALL &&
      (subject !== EPermissionGrantSubjects.HR || isFeatureActivated('is-hr-activated')),
  )

  return (
    <Controller
      control={control}
      name="grants"
      rules={{ required: false }}
      render={({ field: { onChange, value: grants } }) => (
        <fieldset id={id} className="c-relative c-flex c-flex-wrap">
          <div className="c-flex c-gap-8 c-flex-row c-justify-between c-items-center c-flex-1 c-mb-2">
            <div className="c-flex-1">
              <CurecallInputLabel htmlFor={id} label={t('pages.configuration.permissions.permissions')} />
            </div>
            {grants
              .find((grant) => grant?.subject === EPermissionGrantSubjects.ALL)
              ?.actions?.includes(EPermissionGrantActions.ALL) ? (
              <CurecallButton
                type="button"
                appearance={ECurecallButtonAppearance.ALERT}
                onClick={() => {
                  onChange(
                    updatePermissionGrants({
                      allGrants: grants,
                      operation: 'REMOVE',
                      subject: EPermissionGrantSubjects.ALL,
                      action: EPermissionGrantActions.ALL,
                    }),
                  )
                }}
              >
                {t('pages.configuration.permissions.revoke-all-permissions')}
              </CurecallButton>
            ) : (
              <CurecallTooltip
                content={t('pages.configuration.permissions.give-all-permissions-tooltip')}
                tooltipPosition="left"
              >
                <CurecallButton
                  type="button"
                  appearance={ECurecallButtonAppearance.SECONDARY}
                  onClick={() => {
                    onChange(
                      updatePermissionGrants({
                        allGrants: grants,
                        operation: 'ADD',
                        subject: EPermissionGrantSubjects.ALL,
                        action: EPermissionGrantActions.ALL,
                      }),
                    )
                  }}
                >
                  {t('pages.configuration.permissions.give-all-permissions')}
                </CurecallButton>
              </CurecallTooltip>
            )}
          </div>
          <table className="c-w-full c-border c-border-gray-300 c-rounded-lg">
            <thead className="c-bg-gray-50">
              <tr>
                <th className="c-px-4 c-py-3 c-text-left c-font-bold c-text-gray-900 c-border-b c-border-gray-300">
                  {t('pages.configuration.permissions.subjects')}
                </th>
                <th className="c-px-4 c-py-3 c-text-left c-font-bold c-text-gray-900 c-border-b c-border-gray-300">
                  {t('pages.configuration.permissions.grants')}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleSubjects.map((subject: EPermissionGrantSubjects) => (
                <tr key={subject} className="c-border-b c-border-gray-200">
                  <th scope="row" className="c-px-4 c-py-3 c-font-bold c-text-gray-900">
                    <p className="c-mb-1 c-text-start">{t(subjectToIntlTitle[subject])}</p>
                    <p className="c-font-light c-mb-0 c-text-start">
                      {t(subjectToIntelDescription[subject])}
                    </p>
                  </th>
                  <td className="c-px-4 c-py-3">
                    {getAvailableActionsForSubject(subject).length === 1 ? (
                      <CurecallToggle
                        size={ECurecallSize.LARGE}
                        isEnabled={
                          !!grants
                            .find((grant) => grant.subject === subject)
                            ?.actions?.includes(
                              getAvailableActionsForSubject(subject)[0] as EPermissionGrantActions,
                            )
                        }
                        onChange={(selected: boolean) => {
                          onChange(
                            updatePermissionGrants({
                              allGrants: grants,
                              operation: selected ? 'ADD' : 'REMOVE',
                              subject,
                              action: getAvailableActionsForSubject(subject)[0] as EPermissionGrantActions,
                            }),
                          )
                        }}
                      />
                    ) : (
                      <PermissionGrantActionsMultiSelect
                        allGrants={grants}
                        subject={subject}
                        onChange={onChange}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      )}
    />
  )
}

export { PermissionsTableFields }
